import React, { useState, useEffect, useRef } from "react";
import Sidebar from "../../components/commons/Sidebar";
import Swal from "sweetalert2";
import ImageUploader from "../../components/admin/ImageUploader";
import api from "../../api/client";
import {
  getProfile, updateProfile,
  getSkills, createSkill, updateSkill, deleteSkill,
  getEducation, createEducation, updateEducation, deleteEducation,
  getExperience, createExperience, updateExperience, deleteExperience,
} from "../../api/profileAPI";
import { sortExperienceGrouped, calcDuration } from "../../utils/sortExperience";

const Section = ({ title, children }) => (
  <div className="bg-zinc-900/40 border border-zinc-800 rounded-2xl p-6 mb-6">
    <h3 className="text-sm font-black uppercase tracking-widest text-zinc-400 mb-4">{title}</h3>
    {children}
  </div>
);

const Input = ({ label, value, onChange, type = "text", textarea = false }) => (
  <div>
    <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 block mb-1">{label}</label>
    {textarea ? (
      <textarea
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
      />
    ) : (
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
    )}
  </div>
);

const FieldInput = ({ value, onChange, placeholder = "", type = "text", min, max }) => (
  <input
    type={type}
    min={min}
    max={max}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-3 py-1.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
  />
);

const FieldSelect = ({ value, onChange, children }) => (
  <select
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
  >
    {children}
  </select>
);

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const yearOptions = () => {
  const y = new Date().getFullYear();
  return Array.from({ length: y - 1989 + 5 }, (_, i) => y + 5 - i);
};

const MonthYearPicker = ({ label, value, onChange, optional = false }) => {
  const parts = (value || "").split(" ");
  const initMonth = parts.length === 2 ? parts[0] : "";
  const initYear  = parts.length === 2 ? parts[1] : "";
  const [month, setMonth] = useState(initMonth);
  const [year,  setYear]  = useState(initYear);

  useEffect(() => {
    const p = (value || "").split(" ");
    setMonth(p.length === 2 ? p[0] : "");
    setYear(p.length === 2 ? p[1] : "");
  }, [value]);

  const commit = (m, y) => { if (m && y) onChange(`${m} ${y}`); else onChange(""); };

  return (
    <div>
      {label && (
        <label className="text-[10px] font-bold text-zinc-500 uppercase block mb-1">
          {label}{optional && <span className="text-zinc-600 normal-case font-normal ml-1">(empty = Present)</span>}
        </label>
      )}
      <div className="flex gap-1">
        <select value={month} onChange={(e) => { setMonth(e.target.value); commit(e.target.value, year); }}
          className="flex-1 bg-zinc-800 border border-zinc-600 rounded-lg px-2 py-1.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-indigo-500">
          <option value="">Month</option>
          {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={year} onChange={(e) => { setYear(e.target.value); commit(month, e.target.value); }}
          className="w-24 bg-zinc-800 border border-zinc-600 rounded-lg px-2 py-1.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-indigo-500">
          <option value="">Year</option>
          {yearOptions().map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
    </div>
  );
};

// Shared inline action buttons
const EditActions = ({ onSave, onCancel, saving }) => (
  <div className="flex gap-2 mt-3">
    <button onClick={onSave} disabled={saving}
      className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg transition-colors disabled:opacity-60">
      {saving ? "Saving..." : "Save"}
    </button>
    <button onClick={onCancel}
      className="px-4 py-1.5 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 text-xs font-bold rounded-lg transition-colors">
      Cancel
    </button>
  </div>
);

// ─── SKILL ROW ───────────────────────────────────────────────────────────────
const SkillRow = ({ skill, onSaved, onDeleted }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm]       = useState({ name: skill.name, category: skill.category, level: skill.level });
  const [saving, setSaving]   = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await updateSkill(skill.id, form);
      await onSaved();
      setEditing(false);
    } catch { Swal.fire({ icon: "error", text: "Failed to save skill." }); }
    finally { setSaving(false); }
  };

  const del = async () => {
    const { isConfirmed } = await Swal.fire({ title: "Delete this skill?", icon: "warning", showCancelButton: true, confirmButtonColor: "#ef4444", confirmButtonText: "Delete", cancelButtonText: "Cancel" });
    if (!isConfirmed) return;
    try { await deleteSkill(skill.id); await onDeleted(); }
    catch { Swal.fire({ icon: "error", text: "Failed to delete." }); }
  };

  if (editing) {
    return (
      <div className="bg-zinc-800/80 border border-indigo-500/40 rounded-xl px-4 py-3">
        <div className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-[140px]">
            <label className="text-[10px] font-bold text-zinc-500 uppercase block mb-1">Name</label>
            <FieldInput value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} />
          </div>
          <div>
            <label className="text-[10px] font-bold text-zinc-500 uppercase block mb-1">Kategori</label>
            <FieldSelect value={form.category} onChange={(v) => setForm((f) => ({ ...f, category: v }))}>
              <option value="hard">Hard</option>
              <option value="soft">Soft</option>
            </FieldSelect>
          </div>
          <div>
            <label className="text-[10px] font-bold text-zinc-500 uppercase block mb-1">Level (0–100)</label>
            <FieldInput type="number" min="0" max="100" value={form.level} onChange={(v) => setForm((f) => ({ ...f, level: Number(v) }))} />
          </div>
        </div>
        <EditActions onSave={save} onCancel={() => { setEditing(false); setForm({ name: skill.name, category: skill.category, level: skill.level }); }} saving={saving} />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between bg-zinc-800/50 rounded-xl px-4 py-3 group">
      <div>
        <span className="text-white font-semibold text-sm">{skill.name}</span>
        <span className="ml-2 text-[10px] text-zinc-500 uppercase">{skill.category} · {skill.level}</span>
      </div>
      <div className="flex gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={() => setEditing(true)} className="text-indigo-400 hover:text-indigo-300 text-xs font-bold transition-colors">Edit</button>
        <button onClick={del} className="text-zinc-600 hover:text-red-400 text-xs font-bold transition-colors">Delete</button>
      </div>
    </div>
  );
};

// ─── EDUCATION ROW ───────────────────────────────────────────────────────────
const EduRow = ({ edu, onSaved, onDeleted }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm]       = useState({ institution: edu.institution, degree: edu.degree, field: edu.field, start_year: edu.start_year, end_year: edu.end_year });
  const [saving, setSaving]   = useState(false);

  const save = async () => {
    if (!form.institution?.trim()) return Swal.fire({ icon: "warning", text: "Institution is required." });
    setSaving(true);
    try {
      await updateEducation(edu.id, form);
      await onSaved();
      setEditing(false);
    } catch { Swal.fire({ icon: "error", text: "Failed to save education." }); }
    finally { setSaving(false); }
  };

  const del = async () => {
    const { isConfirmed } = await Swal.fire({ title: "Delete this education?", icon: "warning", showCancelButton: true, confirmButtonColor: "#ef4444", confirmButtonText: "Delete", cancelButtonText: "Cancel" });
    if (!isConfirmed) return;
    try { await deleteEducation(edu.id); await onDeleted(); }
    catch { Swal.fire({ icon: "error", text: "Failed to delete." }); }
  };

  if (editing) {
    return (
      <div className="bg-zinc-800/80 border border-indigo-500/40 rounded-xl px-4 py-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <label className="text-[10px] font-bold text-zinc-500 uppercase block mb-1">Institution</label>
            <FieldInput value={form.institution} onChange={(v) => setForm((f) => ({ ...f, institution: v }))} />
          </div>
          <div>
            <label className="text-[10px] font-bold text-zinc-500 uppercase block mb-1">Degree</label>
            <FieldInput value={form.degree} onChange={(v) => setForm((f) => ({ ...f, degree: v }))} />
          </div>
          <div>
            <label className="text-[10px] font-bold text-zinc-500 uppercase block mb-1">Field</label>
            <FieldInput value={form.field} onChange={(v) => setForm((f) => ({ ...f, field: v }))} />
          </div>
          <MonthYearPicker label="Start Date" value={form.start_year} onChange={(v) => setForm((f) => ({ ...f, start_year: v }))} />
          <MonthYearPicker label="End Date" value={form.end_year} onChange={(v) => setForm((f) => ({ ...f, end_year: v }))} optional />
        </div>
        <EditActions onSave={save} onCancel={() => { setEditing(false); setForm({ institution: edu.institution, degree: edu.degree, field: edu.field, start_year: edu.start_year, end_year: edu.end_year }); }} saving={saving} />
      </div>
    );
  }

  return (
    <div className="flex items-start justify-between bg-zinc-800/50 rounded-xl px-4 py-3 group">
      <div>
        <p className="text-white font-semibold text-sm">{edu.institution}</p>
        <p className="text-zinc-400 text-xs">{edu.degree}{edu.degree && edu.field ? " · " : ""}{edu.field}</p>
        <p className="text-zinc-600 text-[10px]">{edu.start_year} – {edu.end_year || "Present"}</p>
      </div>
      <div className="flex gap-3 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-4">
        <button onClick={() => setEditing(true)} className="text-indigo-400 hover:text-indigo-300 text-xs font-bold transition-colors">Edit</button>
        <button onClick={del} className="text-zinc-600 hover:text-red-400 text-xs font-bold transition-colors">Delete</button>
      </div>
    </div>
  );
};

const EMP_TYPES = ["Full-time","Part-time","Self-employed","Freelance","Contract","Internship","Apprenticeship","Seasonal","Bootcamp Student"];

// ─── EXPERIENCE HELPERS ───────────────────────────────────────────────────────
const parseJSON = (val, fallback = []) => { if (Array.isArray(val)) return val; if (typeof val === "string") { try { return JSON.parse(val); } catch { return fallback; } } return fallback; };

// ─── POINTS EDITOR ───────────────────────────────────────────────────────────
const PointsEditor = ({ value = [], onChange }) => {
  const points = Array.isArray(value) ? value : [];

  const update = (i, v) => {
    const next = [...points];
    next[i] = v;
    onChange(next);
  };

  const add = () => onChange([...points, ""]);

  const remove = (i) => onChange(points.filter((_, idx) => idx !== i));

  return (
    <div>
      <label className="text-[10px] font-bold text-zinc-500 uppercase block mb-1">
        Poin Pekerjaan
        <span className="ml-2 text-zinc-600 normal-case font-normal">(optional)</span>
      </label>
      <div className="space-y-1.5">
        {points.map((pt, i) => (
          <div key={i} className="flex gap-1.5 items-center">
            <span className="text-zinc-600 text-xs shrink-0">·</span>
            <input
              value={pt}
              onChange={(e) => update(i, e.target.value)}
              placeholder={`Poin ${i + 1}...`}
              className="flex-1 bg-zinc-800 border border-zinc-600 rounded-lg px-3 py-1.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <button type="button" onClick={() => remove(i)}
              className="text-zinc-600 hover:text-red-400 text-xs px-1 transition-colors shrink-0">✕</button>
          </div>
        ))}
      </div>
      <button type="button" onClick={add}
        className="mt-2 text-xs text-indigo-400 hover:text-indigo-300 font-semibold transition-colors">
        + Add point
      </button>
    </div>
  );
};

// ─── MEDIA EDITOR ────────────────────────────────────────────────────────────
const BASE_URL = process.env.REACT_APP_EXPRESS_API_BASE_URL || "http://localhost:3001";

const resolveImg = (url) => {
  if (!url) return null;
  if (url.startsWith("/uploads/")) return `${BASE_URL}${url}`;
  return url;
};

const BLANK_MEDIA = { img: "", title: "", description: "", link: "" };

const MediaItemEditor = ({ item, index, onChange, onRemove }) => {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/api/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(index, { ...item, img: data.url });
    } catch {
      Swal.fire({ icon: "error", text: "Image upload failed." });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-3 space-y-2">
      <div className="flex gap-3 items-start">
        {/* Thumbnail */}
        <div
          className="relative w-20 h-14 rounded-lg overflow-hidden bg-zinc-800 border border-zinc-700 flex-shrink-0 cursor-pointer group"
          onClick={() => !uploading && fileRef.current?.click()}
        >
          {item.img ? (
            <img src={resolveImg(item.img)} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-zinc-600 text-xs text-center px-1">
              {uploading ? "..." : "Klik upload"}
            </div>
          )}
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-xs">
            {uploading ? "Uploading..." : "Ganti"}
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden"
            onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0])} />
        </div>
        {/* Fields */}
        <div className="flex-1 space-y-1.5">
          <input
            value={item.title}
            onChange={(e) => onChange(index, { ...item, title: e.target.value })}
            placeholder="Judul media..."
            className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-3 py-1.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <input
            value={item.link}
            onChange={(e) => onChange(index, { ...item, link: e.target.value })}
            placeholder="Link (optional)..."
            className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-3 py-1.5 text-xs text-white placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <button type="button" onClick={() => onRemove(index)}
          className="text-zinc-600 hover:text-red-400 text-sm transition-colors shrink-0 mt-1">✕</button>
      </div>
      <textarea
        value={item.description}
        onChange={(e) => onChange(index, { ...item, description: e.target.value })}
        placeholder="Short description (optional)..."
        rows={2}
        className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-3 py-1.5 text-xs text-white placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
      />
      {/* URL manual fallback */}
      <input
        value={item.img}
        onChange={(e) => onChange(index, { ...item, img: e.target.value })}
        placeholder="Atau paste URL gambar..."
        className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-3 py-1.5 text-xs text-zinc-400 placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
    </div>
  );
};

const MediaEditor = ({ value = [], onChange }) => {
  const items = Array.isArray(value) ? value : [];

  const add = () => onChange([...items, { ...BLANK_MEDIA }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i, updated) => {
    const next = [...items];
    next[i] = updated;
    onChange(next);
  };

  return (
    <div>
      <label className="text-[10px] font-bold text-zinc-500 uppercase block mb-2">
        Media / Proyek
        <span className="ml-2 text-zinc-600 normal-case font-normal">(optional)</span>
      </label>
      <div className="space-y-2">
        {items.map((item, i) => (
          <MediaItemEditor key={i} item={item} index={i} onChange={update} onRemove={remove} />
        ))}
      </div>
      <button type="button" onClick={add}
        className="mt-2 text-xs text-indigo-400 hover:text-indigo-300 font-semibold transition-colors">
        + Add media
      </button>
    </div>
  );
};

// ─── ADD FORMS ───────────────────────────────────────────────────────────────
const AddSkillForm = ({ onAdd }) => {
  const [form, setForm]     = useState({ name: "", category: "hard", level: 60 });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try { await onAdd({ ...form, level: Number(form.level) }); setForm({ name: "", category: "hard", level: 60 }); }
    catch { Swal.fire({ icon: "error", text: "Failed to add skill." }); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="flex flex-wrap gap-3 items-end border-b border-zinc-700/50 pb-5 mb-2">
      <div>
        <label className="text-[10px] font-bold text-zinc-500 uppercase block mb-1">Name</label>
        <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="New skill..."
          className="bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-indigo-500" />
      </div>
      <div>
        <label className="text-[10px] font-bold text-zinc-500 uppercase block mb-1">Kategori</label>
        <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
          className="bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-indigo-500">
          <option value="hard">Hard</option>
          <option value="soft">Soft</option>
        </select>
      </div>
      <div>
        <label className="text-[10px] font-bold text-zinc-500 uppercase block mb-1">Level (0–100)</label>
        <input type="number" min="0" max="100" value={form.level}
          onChange={(e) => setForm((f) => ({ ...f, level: e.target.value }))}
          className="bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 w-24" />
      </div>
      <button type="submit" disabled={saving}
        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-xl transition-colors disabled:opacity-60">
        + Add
      </button>
    </form>
  );
};

const AddEduForm = ({ onAdd }) => {
  const BLANK = { institution: "", degree: "", field: "", start_year: "", end_year: "" };
  const [form, setForm]     = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.institution.trim() || !form.start_year) return Swal.fire({ icon: "warning", text: "Institution and start date are required." });
    setSaving(true);
    try { await onAdd(form); setForm(BLANK); }
    catch { Swal.fire({ icon: "error", text: "Failed to add education." }); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-2 md:grid-cols-3 gap-3 items-end border-b border-zinc-700/50 pb-5 mb-2">
      {[["institution","Institution"],["degree","Degree"],["field","Field"]].map(([key,lbl]) => (
        <div key={key}>
          <label className="text-[10px] font-bold text-zinc-500 uppercase block mb-1">{lbl}</label>
          <input value={form[key]} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
            className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </div>
      ))}
      <MonthYearPicker label="Start Date" value={form.start_year} onChange={(v) => setForm((f) => ({ ...f, start_year: v }))} />
      <MonthYearPicker label="End Date" value={form.end_year} onChange={(v) => setForm((f) => ({ ...f, end_year: v }))} optional />
      <button type="submit" disabled={saving}
        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-xl transition-colors disabled:opacity-60 self-end">
        + Add
      </button>
    </form>
  );
};

// Generic experience form — used for both "add new company" and "add role to existing company"
const ExpForm = ({ initialCompany = "", companyLocked = false, onAdd, onCancel }) => {
  const BLANK = { company: initialCompany, role: "", employment_type: "", start_date: "", end_date: "", is_current: false, points: [], media: [] };
  const [form, setForm]     = useState(BLANK);
  const [saving, setSaving] = useState(false);

  // If initialCompany changes (e.g. new group), reset form
  useEffect(() => {
    setForm((f) => ({ ...f, company: initialCompany }));
  }, [initialCompany]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company.trim() || !form.role.trim() || !form.start_date) return Swal.fire({ icon: "warning", text: "Company, role, and start date are required." });
    setSaving(true);
    const payload = { ...form, end_date: form.is_current ? "" : form.end_date, points: form.points.filter((p) => p.trim()), media: form.media.filter((m) => m.title || m.img) };
    try {
      await onAdd(payload);
      setForm({ ...BLANK, company: initialCompany });
      if (onCancel) onCancel();
    }
    catch { Swal.fire({ icon: "error", text: "Failed to add experience." }); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 items-end">
        <div>
          <label className="text-[10px] font-bold text-zinc-500 uppercase block mb-1">Company</label>
          {companyLocked ? (
            <div className="w-full bg-zinc-900/50 border border-zinc-700/50 rounded-xl px-3 py-2 text-sm text-zinc-400 cursor-not-allowed">{form.company}</div>
          ) : (
            <input value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
              className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          )}
        </div>
        <div>
          <label className="text-[10px] font-bold text-zinc-500 uppercase block mb-1">Role</label>
          <input value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
            className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </div>
        <div>
          <label className="text-[10px] font-bold text-zinc-500 uppercase block mb-1">Employment Type</label>
          <select value={form.employment_type} onChange={(e) => setForm((f) => ({ ...f, employment_type: e.target.value }))}
            className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-indigo-500">
            <option value="">— Select type —</option>
            {EMP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <MonthYearPicker label="Start Date" value={form.start_date} onChange={(v) => setForm((f) => ({ ...f, start_date: v }))} />
        {!form.is_current && (
          <MonthYearPicker label="End Date" value={form.end_date} onChange={(v) => setForm((f) => ({ ...f, end_date: v }))} optional />
        )}
        <div className="flex items-center gap-2 self-center pt-4">
          <input type="checkbox" id={`is_current_${initialCompany}`} checked={form.is_current}
            onChange={(e) => setForm((f) => ({ ...f, is_current: e.target.checked, end_date: "" }))}
            className="w-4 h-4 accent-indigo-600" />
          <label htmlFor={`is_current_${initialCompany}`} className="text-xs text-zinc-400">Currently active</label>
        </div>
      </div>
      <PointsEditor value={form.points} onChange={(pts) => setForm((f) => ({ ...f, points: pts }))} />
      <MediaEditor value={form.media} onChange={(m) => setForm((f) => ({ ...f, media: m }))} />
      <div className="flex gap-2">
        <button type="submit" disabled={saving}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-xl transition-colors disabled:opacity-60">
          {saving ? "Saving..." : "+ Add"}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel}
            className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 text-sm font-bold rounded-xl transition-colors">
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};

const AddExpForm = ({ onAdd }) => (
  <div className="border-b border-zinc-700/50 pb-5 mb-2">
    <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-3">Add New Company</p>
    <ExpForm onAdd={onAdd} />
  </div>
);

// ─── DURATION BADGE ───────────────────────────────────────────────────────────
const DurationBadge = ({ start, end, isCurrent }) => {
  const d = calcDuration(start, end, isCurrent);
  if (!d) return null;
  return <span className="text-[10px] text-zinc-500 bg-zinc-800 rounded-full px-2 py-0.5 ml-2 shrink-0">{d}</span>;
};

// ─── COMPANY GROUP ────────────────────────────────────────────────────────────
const CompanyGroup = ({ company, roles, onAdd, onSaved, onDeleted }) => {
  const [showAddRole, setShowAddRole] = useState(false);

  // Total duration: use earliest start → latest end across all roles
  const totalDuration = (() => {
    const starts = roles.map((r) => r.start_date).filter(Boolean);
    const ends = roles.filter((r) => !r.is_current).map((r) => r.end_date).filter(Boolean);
    const hasCurrent = roles.some((r) => r.is_current);
    if (starts.length === 0) return null;
    const earliestStart = starts.reduce((acc, s) => {
      const d = new Date(s.split(" ").reverse().join("-") || s);
      return !acc || d < acc ? s : acc;
    }, null);
    if (hasCurrent) return calcDuration(earliestStart, null, true);
    if (ends.length === 0) return null;
    const latestEnd = ends.reduce((acc, e) => {
      const d = new Date(e.split(" ").reverse().join("-") || e);
      return !acc || d > acc ? e : acc;
    }, null);
    return calcDuration(earliestStart, latestEnd, false);
  })();

  return (
    <div className="border border-zinc-700/60 rounded-2xl overflow-hidden">
      {/* Company header */}
      <div className="flex items-center justify-between bg-zinc-800/60 px-4 py-3">
        <div className="flex items-center gap-2">
          <p className="text-white font-bold text-sm">{company}</p>
          {totalDuration && (
            <span className="text-[10px] text-indigo-300 bg-indigo-900/40 border border-indigo-700/40 rounded-full px-2 py-0.5">
              Total: {totalDuration}
            </span>
          )}
        </div>
        <button
          onClick={() => setShowAddRole((v) => !v)}
          className="text-xs text-indigo-400 hover:text-indigo-300 font-bold transition-colors"
        >
          {showAddRole ? "Cancel" : "+ Add Role"}
        </button>
      </div>

      {/* Add role inline form */}
      {showAddRole && (
        <div className="bg-zinc-900/60 border-b border-zinc-700/60 px-4 py-4">
          <ExpForm
            initialCompany={company}
            companyLocked
            onAdd={onAdd}
            onCancel={() => setShowAddRole(false)}
          />
        </div>
      )}

      {/* Role rows */}
      <div className="divide-y divide-zinc-700/40">
        {roles.map((e) => (
          <div key={e.id} className="px-4 py-1">
            <ExpRowInGroup exp={e} onSaved={onSaved} onDeleted={onDeleted} />
          </div>
        ))}
      </div>
    </div>
  );
};

// Slim version of ExpRow for inside the group (company already shown in header)
const ExpRowInGroup = ({ exp, onSaved, onDeleted }) => {
  const initPoints = parseJSON(exp.points);
  const initMedia  = parseJSON(exp.media);
  const [editing, setEditing] = useState(false);
  const [form, setForm]       = useState({ company: exp.company, role: exp.role, employment_type: exp.employment_type || "", start_date: exp.start_date, end_date: exp.end_date, is_current: !!exp.is_current, points: initPoints, media: initMedia });
  const [saving, setSaving]   = useState(false);

  const resetForm = () => setForm({ company: exp.company, role: exp.role, employment_type: exp.employment_type || "", start_date: exp.start_date, end_date: exp.end_date, is_current: !!exp.is_current, points: initPoints, media: initMedia });

  const save = async () => {
    if (!form.role?.trim()) return Swal.fire({ icon: "warning", text: "Role is required." });
    setSaving(true);
    const payload = { ...form, end_date: form.is_current ? "" : form.end_date, points: form.points.filter((p) => p.trim()), media: form.media.filter((m) => m.title || m.img) };
    try { await updateExperience(exp.id, payload); await onSaved(); setEditing(false); }
    catch { Swal.fire({ icon: "error", text: "Failed to save experience." }); }
    finally { setSaving(false); }
  };

  const del = async () => {
    const { isConfirmed } = await Swal.fire({ title: "Delete this role?", icon: "warning", showCancelButton: true, confirmButtonColor: "#ef4444", confirmButtonText: "Delete", cancelButtonText: "Cancel" });
    if (!isConfirmed) return;
    try { await deleteExperience(exp.id); await onDeleted(); }
    catch { Swal.fire({ icon: "error", text: "Failed to delete." }); }
  };

  if (editing) {
    return (
      <div className="py-3 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <label className="text-[10px] font-bold text-zinc-500 uppercase block mb-1">Role</label>
            <FieldInput value={form.role} onChange={(v) => setForm((f) => ({ ...f, role: v }))} />
          </div>
          <div>
            <label className="text-[10px] font-bold text-zinc-500 uppercase block mb-1">Employment Type</label>
            <FieldSelect value={form.employment_type} onChange={(v) => setForm((f) => ({ ...f, employment_type: v }))}>
              <option value="">— Select type —</option>
              {EMP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </FieldSelect>
          </div>
          <MonthYearPicker label="Start Date" value={form.start_date} onChange={(v) => setForm((f) => ({ ...f, start_date: v }))} />
          {!form.is_current && (
            <MonthYearPicker label="End Date" value={form.end_date} onChange={(v) => setForm((f) => ({ ...f, end_date: v }))} optional />
          )}
          <div className="flex items-center gap-2 self-center pt-3">
            <input type="checkbox" id={`curr-${exp.id}`} checked={form.is_current}
              onChange={(e) => setForm((f) => ({ ...f, is_current: e.target.checked, end_date: "" }))}
              className="w-4 h-4 accent-indigo-600" />
            <label htmlFor={`curr-${exp.id}`} className="text-xs text-zinc-400">Currently active</label>
          </div>
        </div>
        <PointsEditor value={form.points} onChange={(pts) => setForm((f) => ({ ...f, points: pts }))} />
        <MediaEditor value={form.media} onChange={(m) => setForm((f) => ({ ...f, media: m }))} />
        <EditActions onSave={save} onCancel={() => { setEditing(false); resetForm(); }} saving={saving} />
      </div>
    );
  }

  return (
    <div className="flex items-start justify-between py-3 group">
      <div className="flex-1 min-w-0">
        <div className="flex items-center flex-wrap gap-1">
          <p className="text-white font-semibold text-sm">{exp.role}</p>
          {exp.employment_type && <span className="text-[10px] text-zinc-500">· {exp.employment_type}</span>}
          <DurationBadge start={exp.start_date} end={exp.end_date} isCurrent={exp.is_current} />
        </div>
        <p className="text-zinc-600 text-[10px]">{exp.start_date} – {exp.is_current ? "Present" : (exp.end_date || "—")}</p>
        {initPoints.length > 0 && (
          <ul className="mt-1.5 space-y-0.5">
            {initPoints.map((pt, i) => (
              <li key={i} className="flex gap-2 text-zinc-400 text-xs">
                <span className="text-zinc-600 shrink-0">·</span>
                <span>{pt}</span>
              </li>
            ))}
          </ul>
        )}
        {initMedia.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {initMedia.map((m, i) => (
              <div key={i} className="flex items-center gap-2 bg-zinc-900 border border-zinc-700 rounded-lg px-2 py-1.5 max-w-[200px]">
                {m.img && <img src={resolveImg(m.img)} alt="" className="w-8 h-8 rounded object-cover shrink-0" />}
                <div className="min-w-0">
                  <p className="text-white text-xs font-semibold truncate">{m.title || "Media"}</p>
                  {m.description && <p className="text-zinc-500 text-[10px] truncate">{m.description}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="flex gap-3 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-4 pt-0.5">
        <button onClick={() => setEditing(true)} className="text-indigo-400 hover:text-indigo-300 text-xs font-bold transition-colors">Edit</button>
        <button onClick={del} className="text-zinc-600 hover:text-red-400 text-xs font-bold transition-colors">Delete</button>
      </div>
    </div>
  );
};

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
const ProfileAdmin = () => {
  const [tab, setTab]       = useState("profile");
  const [profile, setProfile] = useState({});
  const [skills, setSkills] = useState([]);
  const [edu, setEdu]       = useState([]);
  const [exp, setExp]       = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [pr, sk, ed, ex] = await Promise.all([getProfile(), getSkills(), getEducation(), getExperience()]);
      setProfile(pr ?? {});
      setSkills(Array.isArray(sk) ? sk : []);
      setEdu(Array.isArray(ed) ? ed : []);
      setExp(Array.isArray(ex) ? ex : []);
    } catch {
      Swal.fire({ icon: "error", text: "Failed to load profile data." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveProfile = async () => {
    try {
      const saved = await updateProfile(profile);
      // Merge response into current state so no field disappears if backend omits it
      setProfile((prev) => ({ ...prev, ...(saved ?? {}) }));
      Swal.fire({ icon: "success", title: "Profile saved!", timer: 1200, showConfirmButton: false });
    } catch {
      Swal.fire({ icon: "error", text: "Failed to save profile." });
    }
  };

  const tabs = ["profile", "skills", "education", "experience"];

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-900">
      <Sidebar />
      <div className="flex-1 p-6 lg:p-10 overflow-auto">
        <div className="mb-6">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-indigo-400">Management</p>
          <h2 className="text-3xl font-black text-white">PROFILE & RESUME</h2>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {tabs.map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-widest transition-all ${tab === t ? "bg-indigo-600 text-white" : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"}`}>
              {t}
            </button>
          ))}
        </div>

        {loading ? <p className="text-zinc-500">Loading...</p> : (
          <>
            {/* Profile Tab */}
            {tab === "profile" && (
              <>
                <Section title="Profile Information">
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input label="Full Name" value={profile.full_name} onChange={(v) => setProfile((p) => ({ ...p, full_name: v }))} />
                    <div className="md:col-span-2">
                      <ImageUploader
                        value={profile.photo_url}
                        onChange={(v) => setProfile((p) => ({ ...p, photo_url: v }))}
                        label="Profile Photo"
                      />
                    </div>
                    <Input label="Headline" value={profile.headline} onChange={(v) => setProfile((p) => ({ ...p, headline: v }))} />
                    <Input label="Doing Now" value={profile.doing_now} onChange={(v) => setProfile((p) => ({ ...p, doing_now: v }))} />
                    <div className="md:col-span-2">
                      <Input label="Description" value={profile.description} onChange={(v) => setProfile((p) => ({ ...p, description: v }))} textarea />
                    </div>
                    <div className="md:col-span-2">
                      <Input label="Summary" value={profile.summary} onChange={(v) => setProfile((p) => ({ ...p, summary: v }))} textarea />
                    </div>
                  </div>
                </Section>

                <Section title="Social Links">
                  <p className="text-[11px] text-zinc-500 mb-4">Enter full URLs or handles. Toggle the eye icon to show/hide on public profile.</p>
                  <div className="space-y-3">
                    {[
                      { key: "social_github",    showKey: "show_github",    label: "GitHub",    placeholder: "https://github.com/username", icon: "M12 2C6.477 2 2 6.477 2 12c0 4.418 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.604-3.369-1.34-3.369-1.34-.454-1.155-1.11-1.463-1.11-1.463-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.115 2.504.337 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.202 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.933.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.741 0 .267.18.578.688.48C19.138 20.163 22 16.418 22 12c0-5.523-4.477-10-10-10z" },
                      { key: "social_instagram", showKey: "show_instagram", label: "Instagram", placeholder: "https://instagram.com/username", icon: "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" },
                      { key: "social_linkedin",  showKey: "show_linkedin",  label: "LinkedIn",  placeholder: "https://linkedin.com/in/username", icon: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" },
                      { key: "social_email",     showKey: "show_email",     label: "Email",     placeholder: "hello@example.com", icon: "M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" },
                      { key: "social_phone",     showKey: "show_phone",     label: "Phone",     placeholder: "+62 812 3456 7890", icon: "M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" },
                    ].map(({ key, showKey, label, placeholder, icon }) => (
                      <div key={key} className="flex items-center gap-3">
                        <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-zinc-800 text-zinc-400">
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                            <path d={icon} />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 block mb-1">{label}</label>
                          <input
                            type="text"
                            value={profile[key] ?? ""}
                            onChange={(e) => setProfile((p) => ({ ...p, [key]: e.target.value }))}
                            placeholder={placeholder}
                            className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                        <button
                          onClick={() => setProfile((p) => ({ ...p, [showKey]: p[showKey] ? 0 : 1 }))}
                          title={profile[showKey] ? "Shown on profile" : "Hidden from profile"}
                          className={`mt-4 flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg border transition-colors ${
                            profile[showKey]
                              ? "bg-indigo-600/20 border-indigo-500/40 text-indigo-400 hover:bg-indigo-600/30"
                              : "bg-zinc-800 border-zinc-700 text-zinc-600 hover:text-zinc-400"
                          }`}
                        >
                          {profile[showKey] ? (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 4.411m0 0L21 21" />
                            </svg>
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </Section>

                <Section title="What I Do Section">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-zinc-300 font-semibold">Show "What I Do" section</p>
                      <p className="text-[11px] text-zinc-500 mt-0.5">Cybersecurity · NOC · Full-Stack cards visible on portfolio</p>
                    </div>
                    <button
                      onClick={() => setProfile((p) => ({ ...p, show_what_i_do: p.show_what_i_do ? 0 : 1 }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${profile.show_what_i_do ? "bg-indigo-600" : "bg-zinc-700"}`}
                    >
                      <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${profile.show_what_i_do ? "translate-x-6" : "translate-x-1"}`} />
                    </button>
                  </div>
                </Section>

                <Section title="CV / Resume">
                  <div className="space-y-4">
                    {[
                      { key: "cv_pdf_url", label: "PDF", accept: ".pdf", ext: "PDF", color: "red" },
                      { key: "cv_docx_url", label: "DOCX", accept: ".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document", ext: "DOCX", color: "blue" },
                    ].map(({ key, label, accept, ext, color }) => (
                      <div key={key} className="flex items-center gap-3">
                        <div className={`w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-zinc-800 text-${color}-400 text-[10px] font-black`}>
                          {ext}
                        </div>
                        <div className="flex-1">
                          <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 block mb-1">{label} URL</label>
                          <input
                            type="text"
                            value={profile[key] ?? ""}
                            onChange={(e) => setProfile((p) => ({ ...p, [key]: e.target.value }))}
                            placeholder={`https://.../${label.toLowerCase()}`}
                            className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                        <div className="mt-4 flex-shrink-0">
                          <label className="cursor-pointer px-3 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 text-xs font-bold rounded-xl transition-colors">
                            Upload
                            <input
                              type="file"
                              accept={accept}
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files[0];
                                if (!file) return;
                                const fd = new FormData();
                                fd.append("file", file);
                                try {
                                  const { data } = await api.post("/api/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                                  setProfile((p) => ({ ...p, [key]: data.url }));
                                } catch {
                                  Swal.fire({ icon: "error", text: `Failed to upload ${label}.` });
                                }
                                e.target.value = "";
                              }}
                            />
                          </label>
                        </div>
                        {profile[key] && (
                          <a
                            href={profile[key].startsWith("/uploads/") ? `${process.env.REACT_APP_EXPRESS_API_BASE_URL || "http://localhost:3001"}${profile[key]}` : profile[key]}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-4 flex-shrink-0 text-xs text-indigo-400 hover:text-indigo-300 underline"
                          >
                            Preview
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </Section>

                <button onClick={saveProfile} className="mb-6 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-colors">
                  Save Profile
                </button>
              </>
            )}

            {/* Skills Tab */}
            {tab === "skills" && (
              <Section title="Skills">
                <AddSkillForm onAdd={async (data) => { await createSkill(data); await load(); }} />
                <div className="space-y-2 mt-4">
                  {skills.length === 0 && <p className="text-zinc-600 text-sm">No skills yet.</p>}
                  {skills.map((s) => (
                    <SkillRow key={s.id} skill={s} onSaved={load} onDeleted={load} />
                  ))}
                </div>
              </Section>
            )}

            {/* Education Tab */}
            {tab === "education" && (
              <Section title="Pendidikan">
                <AddEduForm onAdd={async (data) => { await createEducation(data); await load(); }} />
                <div className="space-y-2 mt-4">
                  {edu.length === 0 && <p className="text-zinc-600 text-sm">No education records yet.</p>}
                  {edu.map((e) => (
                    <EduRow key={e.id} edu={e} onSaved={load} onDeleted={load} />
                  ))}
                </div>
              </Section>
            )}

            {/* Experience Tab */}
            {tab === "experience" && (
              <Section title="Pengalaman">
                <AddExpForm onAdd={async (data) => { await createExperience(data); await load(); }} />
                <div className="space-y-4 mt-4">
                  {exp.length === 0 && <p className="text-zinc-600 text-sm">No experience records yet.</p>}
                  {sortExperienceGrouped(exp).map(([company, roles]) => (
                    <CompanyGroup
                      key={company}
                      company={company}
                      roles={roles}
                      onAdd={async (data) => { await createExperience(data); await load(); }}
                      onSaved={load}
                      onDeleted={load}
                    />
                  ))}
                </div>
              </Section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProfileAdmin;
